import React from "react";
import { BookOpen, Check, PenLine, Package, ArrowRight } from "lucide-react";
import { bundleCompare } from "../mock";
import useReveal from "../hooks/useReveal";

const chooseBundle = () => {
  window.dispatchEvent(new CustomEvent("select-option", { detail: "bundle" }));
  document.getElementById("preorder")?.scrollIntoView({ behavior: "smooth" });
};

const Item = ({ text, strong }) => (
  <li className="flex items-start gap-2.5">
    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${strong ? "text-[#990EE5]" : "text-[#6618D0]"}`} />
    <span className={`text-[14px] leading-relaxed ${strong ? "font-semibold text-[#2a2a2a]" : "text-[#4a4a4a]"}`}>
      {text}
    </span>
  </li>
);

export default function BundleValue() {
  const [ref, inView] = useReveal();

  return (
    <section id="bundle" data-testid="bundle-value-section" className="bg-[#EAE9F6] py-16 md:py-20">
      <div ref={ref} className={`reveal ${inView ? "in-view" : ""} mx-auto max-w-6xl px-5 md:px-8`}>
        <p className="text-xs font-extrabold tracking-[0.15em] text-[#990EE5]">
          READING IS WHERE IT STARTS. PRACTICE IS WHERE IT CHANGES.
        </p>
        <h2 className="mt-2 max-w-2xl font-serif-display text-3xl font-bold leading-tight text-[#6618D0] md:text-[2.5rem]">
          Why most parents choose the Book + Workbook
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#4a4a4a]">
          Understanding a pattern and changing it are two different steps. The book
          gives you the first. The workbook walks you through the second, using your
          own family moments, one page at a time.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-5">
          <div data-testid="bundle-compare-book" className="rounded-2xl border border-[#D8CFF0] bg-white/60 p-7 md:col-span-2 md:self-start">
            <div className="flex items-center justify-between">
              <BookOpen className="h-7 w-7 text-[#6618D0]" strokeWidth={1.7} />
              <span className="font-serif-display text-xl font-bold text-[#6618D0]">$30</span>
            </div>
            <h3 className="mt-4 font-serif-display text-xl font-bold text-[#6618D0]">The Book</h3>
            <p className="mt-1 text-[13px] font-semibold text-[#7a7a7a]">You'll understand:</p>
            <ul className="mt-4 space-y-3">
              {bundleCompare.book.map((t) => <Item key={t} text={t} />)}
            </ul>
          </div>

          <div data-testid="bundle-compare-bundle" className="relative rounded-2xl border-2 border-[#6618D0] bg-white p-7 shadow-xl md:col-span-3">
            <span className="absolute -top-3 left-7 rounded-full bg-[#990EE5] px-3 py-1 text-[10px] font-extrabold tracking-wide text-white">
              RECOMMENDED
            </span>
            <div className="flex items-center justify-between">
              <PenLine className="h-7 w-7 text-[#990EE5]" strokeWidth={1.7} />
              <span className="font-serif-display text-xl font-bold text-[#6618D0]">$40</span>
            </div>
            <h3 className="mt-4 font-serif-display text-xl font-bold text-[#6618D0]">Book + Workbook</h3>
            <p className="mt-1 text-[13px] font-semibold text-[#7a7a7a]">You'll understand, and you'll practise:</p>
            <ul className="mt-4 space-y-3">
              <Item text="Everything in the book" />
              {bundleCompare.bundle.map((t) => <Item key={t} text={t} strong />)}
            </ul>
            <div className="mt-6 flex items-center gap-2 rounded-lg bg-[#F3EEFC] px-4 py-3 text-[13px] text-[#4a4a4a]">
              <Package className="h-4 w-4 shrink-0 text-[#6618D0]" />
              Both printed and shipped together in one package.
            </div>
            <button
              data-testid="bundle-choose-btn"
              onClick={chooseBundle}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-[#6618D0] px-6 py-3.5 font-serif-display text-sm font-bold tracking-wide text-white shadow-sm transition-colors hover:bg-[#5512b0]"
            >
              CHOOSE THE BOOK + WORKBOOK — $40 <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <p className="mt-8 font-serif-display text-lg text-[#2a2a2a] md:text-xl">
          For $10 more, the ideas in the book become a plan you can follow at home.
        </p>
      </div>
    </section>
  );
}
